// Customer context (SPEC.md §6.1). New scope and not fully specified, so this is
// deliberately the cheap, high-value half: who we think is calling, which store,
// and what else is open — the questions an agent otherwise answers by opening a
// second tab mid-call.
//
// Recent tickets are split into three collapsible scopes — Customer, Store,
// Brand — with Customer and Store open and Brand closed. The desk's words map to
// the schema as Store = `locations`, Brand = `organizations` (migration 004:
// "Locations (stores) under organizations").
//
// ONE request, not three. `GET /api/tickets` (§3.1) filters on
// `organization_id` and on nothing narrower: `buildFilters` in
// src/services/tickets.js reads publicRef, q, status, assigned_user_id,
// queue_id, organization_id and channel, and no `customer_id` or `location_id`
// exists anywhere in that path. Sending them anyway would not narrow anything —
// the validator would accept them and the service would drop them — so the
// "Customer" group would quietly be the whole visible queue. Grouping in the
// browser is the version that cannot lie. The price is truncation, handled
// below rather than hidden. Adding those two filters for real is a change to
// src/services/tickets.js, not to the route alone; see the report.
//
// The store hint: under the store's details, one line per other agent who has an open draft
// for the same store ("Pat started a ticket for this store 12m ago"). Names and ages only;
// the endpoint returns nothing else (docs/API.md §12.8). Each read is stored with the store
// id it was made for and shown only while that is still the chosen store, so a late answer
// for a previous store is never shown. A failed read shows nothing.
//
// Links open in a new tab: leaving this route mid-call is exactly what draft
// preservation exists to survive, and not needing it at all is better.

import { Suspense, lazy, useEffect, useMemo, useState } from 'react';

import { api } from '../../api/client.js';
import { groupRecentTickets, initialExpanded } from './recent-ticket-groups.js';

// Lazy: this screen is in the eager bundle, and the store panel is only needed once a store
// is chosen. Imported statically it added ~3.6 kB to eager JS (Support's D-045 ceiling);
// lazily, main keeps only the chunk reference.
const StorePanel = lazy(() => import('./StorePanel.jsx'));
import { hintRowsFor, hintSentence } from '../drafts/team-drafts.js';
import { supportPath } from '../../support-path.js';

// The §3.1 maximum (src/lib/pagination.js, PAGE_LIMIT_MAX). The old flat list
// asked for 5, which was fine when 5 was the whole answer; three groups carved
// out of one sample need the widest sample the endpoint will give, or a caller's
// own tickets fall off the end of their brand's page and the Customer group
// reads "none on file" for somebody who called last week.
const SAMPLE_LIMIT = 100;

export default function CustomerContext({ organization, location, customer }) {
  const [recent, setRecent] = useState({ status: 'idle', rows: [], total: null });
  // Per-viewer UI state, deliberately not persisted: nobody asked for a sticky
  // panel, and a remembered "Brand open" would quietly repeal the default.
  const [expanded, setExpanded] = useState(initialExpanded);
  // The store hint's last answer, `{ locationId, rows, fetchedAt }`, or null.
  const [hint, setHint] = useState(null);
  const locationId = location?.id ?? null;

  useEffect(() => {
    if (locationId == null) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    api
      .get(`/ticket-drafts/for-location/${encodeURIComponent(locationId)}`, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        setHint({
          locationId,
          rows: Array.isArray(response?.data) ? response.data : [],
          fetchedAt: Date.now(),
        });
      })
      // A convenience: a failed read shows nothing.
      .catch(() => { if (!cancelled) setHint(null); });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [locationId]);

  // Whatever was stored, only an answer for the store chosen NOW is shown.
  const hintRows = hintRowsFor(hint, locationId);

  useEffect(() => {
    if (!organization) {
      setRecent({ status: 'idle', rows: [], total: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setRecent({ status: 'loading', rows: [], total: null });

    api
      .get(
        `/tickets?organization_id=${encodeURIComponent(organization.id)}` +
          `&limit=${SAMPLE_LIMIT}&sort=-updated_at`,
        { signal: controller.signal },
      )
      .then((response) => {
        if (cancelled) return;
        setRecent({
          status: 'ready',
          rows: response.data ?? [],
          // A real COUNT over the filter, ignoring limit/offset — the one number
          // here that a page size cannot corrupt.
          total: response.page?.total ?? null,
        });
      })
      .catch((error) => {
        // Context is a convenience; a failure here must not look like the create
        // form is broken.
        if (!cancelled && error?.name !== 'AbortError') {
          setRecent({ status: 'error', rows: [], total: null });
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [organization]);

  const grouped = useMemo(
    () =>
      groupRecentTickets({
        rows: recent.rows,
        total: recent.total,
        customer,
        location,
        organization,
      }),
    [recent, customer, location, organization],
  );

  const toggle = (key) => setExpanded((current) => ({ ...current, [key]: !current[key] }));

  return (
    <aside className="rounded-lg border border-line bg-surface p-4">
      <h2 className="text-sm font-semibold text-ink">Customer context</h2>

      {!customer && !organization && (
        <p className="mt-2 text-sm text-ink-muted">No caller identified yet.</p>
      )}

      {customer && (
        <dl className="mt-2 space-y-1 text-sm">
          <Row label="Name" value={customer.name} />
          <Row label="Email" value={customer.email} />
          <Row label="Phone" value={customer.phone} />
          <Row label="Company" value={customer.organization?.name ?? organization?.name ?? customer.company} />
        </dl>
      )}

      {location && (
        <dl className="mt-3 space-y-1 border-t border-line pt-3 text-sm">
          <Row label="Store" value={location.name} />
          <Row label="Account" value={location.account_number} />
          <Row label="Address" value={location.address} />
          <Row label="Store phone" value={location.phone} />
        </dl>
      )}

      {/* The store in Inventory and its Mesh stations (store-panel.js). */}
      {/* No fallback: the panel is a convenience, and nothing shows until its chunk is in. */}
      {location && (
        <Suspense fallback={null}>
          <StorePanel location={location} />
        </Suspense>
      )}

      {location && hintRows.length > 0 && (
        <ul
          aria-label="Other open drafts for this store"
          className="mt-3 space-y-1 border-t border-line pt-3 text-sm text-ink-secondary"
        >
          {hintRows.map((row, index) => (
            <li key={index}>{hintSentence(row, hint.fetchedAt)}</li>
          ))}
        </ul>
      )}

      {(customer || organization) && (
        <div className="mt-3 border-t border-line pt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
            Recent tickets
          </h3>
          {/* These were slate-400, which is 3.1:1 on white — under AA before dark
              mode was ever in the picture. text-ink-muted is the de-emphasised token
              and is >= 4.5:1 on every surface it is allowed to sit on. */}
          {recent.status === 'loading' && <p className="mt-1 text-sm text-ink-muted">Loading…</p>}
          {recent.status === 'error' && <p className="mt-1 text-sm text-ink-muted">Unavailable.</p>}

          {/* No organization means no lookup happened at all. Rendering three
              empty groups here would assert "no tickets on file" about a search
              nobody ran — the caller may well have a history. */}
          {recent.status === 'idle' && (
            <p className="mt-1 text-sm text-ink-muted">
              Recent tickets are looked up by organization, and this caller has none on file.
            </p>
          )}

          {recent.status === 'ready' && (
            <>
              <div className="mt-1">
                {grouped.groups.map((group) => (
                  <TicketGroup
                    key={group.key}
                    group={group}
                    expanded={expanded[group.key]}
                    onToggle={() => toggle(group.key)}
                  />
                ))}
              </div>
              {grouped.truncated && (
                <p className="mt-2 text-xs text-ink-muted">
                  Only the {grouped.sampleSize} most recently updated of this brand&rsquo;s{' '}
                  {recent.total} tickets were checked, so the Customer and Store lists may be
                  short and are not counted.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </aside>
  );
}

/**
 * One collapsible scope.
 *
 * A real `<button>` carrying `aria-expanded` and `aria-controls`, not a clickable
 * `<div>`: that is what makes Enter/Space work and what makes a screen reader
 * announce the state. The panel stays in the DOM and is hidden with the `hidden`
 * attribute rather than unmounted, so `aria-controls` always resolves to a node.
 */
function TicketGroup({ group, expanded, onToggle }) {
  const panelId = `recent-tickets-${group.key}`;
  return (
    <div className="border-b border-line last:border-b-0">
      <h4>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          className="flex w-full items-center gap-2 rounded px-1 py-2 text-left hover:bg-sunken"
        >
          <span
            aria-hidden="true"
            className={`shrink-0 text-ink-muted transition-transform ${expanded ? 'rotate-90' : ''}`}
          >
            &#9656;
          </span>
          <span className="text-xs font-semibold uppercase tracking-wide text-ink">
            {group.label}
          </span>
          {/* The scope's name, because these groups overlap: a ticket from this
              caller at this store is in all three. Naming the thing each group is
              scoped to turns the repeat into drill-down rather than a bug. */}
          {group.scopeName && (
            <span className="min-w-0 flex-1 truncate text-xs text-ink-muted">{group.scopeName}</span>
          )}
          {/* No count rather than a page-size artefact — `count` is null exactly
              when the number could not be proven. */}
          {group.count !== null && (
            <span className="ml-auto shrink-0 text-xs tabular-nums text-ink-muted">
              {group.count}
            </span>
          )}
        </button>
      </h4>

      <div id={panelId} hidden={!expanded} className="pb-2 pl-5">
        {!group.available && <p className="text-sm text-ink-muted">{group.unavailableReason}</p>}

        {group.available && group.tickets.length === 0 && (
          <p className="text-sm text-ink-muted">None on file.</p>
        )}

        {group.tickets.length > 0 && (
          <ul className="space-y-2">
            {group.tickets.map((ticket) => (
              <li key={ticket.id} className="text-sm">
                <a
                  href={supportPath(`/tickets/${ticket.public_ref}`)}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-brand-700 hover:underline dark:text-brand-300"
                >
                  #{ticket.public_ref}
                </a>{' '}
                <span className={`badge badge-${ticket.status}`}>{ticket.status}</span>
                <div className="truncate text-ink-secondary">{ticket.subject}</div>
              </li>
            ))}
          </ul>
        )}

        {group.moreCount > 0 && (
          <p className="mt-2 text-xs text-ink-muted">and {group.moreCount} older</p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-ink-secondary">{label}</dt>
      <dd className="min-w-0 truncate text-ink">{value}</dd>
    </div>
  );
}
